import React, { SetStateAction, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ConfirmationModal } from '../../../../../../../shared/components/modal/ConfirmationModal';
import { iManageAttachments, iManageList } from '../../dto/IManageCalendar';
import { CalendarCreateModalHeader } from './CalendarCreateModalHeader';
import { CalendarModalBody } from './CalendarModalBody';
import { useTranslation } from 'react-i18next';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';
import { InfluencerPlannerService } from '../../../../../../influencer/planner/service';
import { ErrorToast, SuccessToast } from '../../../../../../../utils/toasters';
import { Campaign } from '../../../../../../../model/campaign/Campaign';
import { hasAccessRights } from '../../../../../../../shared/functions/Functions';
import { AccessRightEnum } from '../../../../../../../enum/AccessRightEnum';

export const statusesToHideApproveButton = ['approved', 'reported'];

interface iModalProps {
    show: boolean;
    closeModal: () => void;
    onSend: () => void;
    state: any;
    setState: React.Dispatch<SetStateAction<iManageList>>;
    setSelectedEventId?: React.Dispatch<SetStateAction<number>>;
    campaignId?: number;
    triggerUpdate?: boolean;
    campaign?: Campaign;
}

export const CalendarUpdateModal = ({ show, closeModal, state, setState, onSend, setSelectedEventId }: iModalProps) => {
    const { t } = useTranslation();

    const [isConfirmationOpened, setIsConfirmationOpened] = useState(false);
    const [hasTextInsideField, setHasTextInsideField] = useState(false);
    const isFieldDisabled = state?.status === 'proposed';
    const isAllFilesApproved = (state?.attachments as iManageAttachments[])?.every(file => file.isApproved);
    const hasAttachments = !!(state?.attachments as iManageAttachments[])?.length;

    const isCaptionVisible = !['instagram_story'].includes(state?.offerItem?.socialPlatform?.name);
    const captionCondition = !state?.isCaptionLocked && isCaptionVisible;
    const showInfluencerFeedback = !hasAccessRights(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE);

    const updateCaptionAndStorybook = async () => {
        await InfluencerPlannerService.updateCaption({
            eventId: state?.id,
            caption: state?.caption,
            storyline: state?.storyline,
        })
            .then(() => {
                closeModal();
                // setUpdateReports && setUpdateReports(prev => !prev);
                SuccessToast(t('influencer.planner.successUpdated'));
            })
            .catch(error => ErrorToast(error));
    };

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if (setState) {
            setState(prev => ({ ...prev, [name]: value }));
        }
    };

    return (
        <>
            <Modal
                show={show}
                onHide={closeModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                size="xl"
                className="calendar-update-modal"
            >
                <Modal.Header className="pb-md-0">
                    <Modal.Title className="w-100">
                        <CalendarCreateModalHeader state={state} t={t} setSelectedEventId={setSelectedEventId!} />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="max-h-60vh-sm ">
                    <CalendarModalBody
                        state={state}
                        setState={setState}
                        setHasTextInsideField={setHasTextInsideField}
                        changeCaptionHandler={changeHandler}
                    />
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between align-items-end">
                    <Button variant="outline-primary" className="btn-sm" onClick={closeModal}>
                        {t('general.button.close')}
                    </Button>
                    <div className="d-flex align-items-center">
                        {['scheduled', 'feedback_loop'].includes(state?.status) && showInfluencerFeedback && (
                            <Button
                                variant="primary"
                                onClick={updateCaptionAndStorybook}
                                disabled={(!state?.caption?.length && !state?.storyline?.length) || hasTextInsideField}
                            >
                                {t('general.button.update')}
                            </Button>
                        )}
                        <TooltipOverlay
                            className="ms-2"
                            content={
                                !hasAttachments || captionCondition || !isAllFilesApproved || hasTextInsideField ? (
                                    <>
                                        {hasTextInsideField && <span>{t('general.deleteSend')}</span>}
                                        {(!hasAttachments || captionCondition || !isAllFilesApproved) && (
                                            <span>{t('brand.campaign.manage.noAttachments')}</span>
                                        )}
                                    </>
                                ) : undefined
                            }
                        >
                            <Button
                                disabled={
                                    statusesToHideApproveButton?.includes(state?.status) ||
                                    !isAllFilesApproved ||
                                    hasTextInsideField ||
                                    !hasAttachments ||
                                    captionCondition
                                }
                                className={`btn-primary ${isFieldDisabled ? 'invisible' : ''} `}
                                onClick={() => {
                                    setIsConfirmationOpened(true);
                                    closeModal();
                                }}
                            >
                                {t(
                                    statusesToHideApproveButton?.includes(state?.status)
                                        ? 'brand.campaign.manage.approved'
                                        : 'brand.campaign.manage.modal.approvePosting'
                                )}
                            </Button>
                        </TooltipOverlay>
                    </div>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                title={t('brand.campaign.book.offer.modal.are_you_sure')}
                description={t('brand.campaign.manage.confirmApproving')}
                show={isConfirmationOpened}
                closeModal={() => setIsConfirmationOpened(false)}
                action={onSend}
            />
        </>
    );
};
